/*
 * Copyright Mimic Networks, Inc. 2025.
 */

/* eslint-disable react/prop-types */
import { EyeOutlined } from '@ant-design/icons';
import { Collapse, Typography } from 'antd';
import React, { useMemo } from 'react';

import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';

import { generalPanelStyle, sectionCardStyle, sectionIconStyle, sectionTitleStyle } from './utils/styles';

const { Panel } = Collapse;
const { Paragraph } = Typography;

interface GeneralConfigSectionProps {
  general: Record<string, unknown>;
}

const renderValue = (value: unknown): string => {
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

export const GeneralConfigSection = React.memo<GeneralConfigSectionProps>(({ general }) => {
  const { t } = useMimicTranslation('configPrettyViewer');

  const generalEntries = useMemo(() => Object.entries(general), [general]);

  return (
    <div style={sectionCardStyle}>
      <Flex vertical gap="md">
        <Flex align="center" gap="sm">
          <EyeOutlined style={{ color: sectionIconStyle.color }} />
          <Text style={sectionTitleStyle}>{t('general.title')}</Text>
        </Flex>

        <Collapse ghost>
          {generalEntries.map(([key, value]) => (
            <Panel header={<Text style={{ fontWeight: 500 }}>{key}</Text>} key={key}>
              <Paragraph style={generalPanelStyle}>{renderValue(value)}</Paragraph>
            </Panel>
          ))}
        </Collapse>
      </Flex>
    </div>
  );
});
